import React from "react";
import { useNavigate } from "react-router";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { put } from "@common/api/apiShared.ts";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import ExcludedUsersField from "../ExcludedUsersField.tsx";
import type { ProcessFieldModel } from "../../ProcessFieldRenderers.tsx";

export interface ProcessExcludedUsersFieldProps {
    processId: string;
    process: ProcessFieldModel | undefined;
    isLoading: boolean;
    onChange: (changeFunc: (process: ProcessFieldModel) => void) => Promise<void>;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
}

function ProcessExcludedUsersField(props: ProcessExcludedUsersFieldProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();

    const removeFromExcludedMutation = useDotLegalMutation((userId: string) =>
        put(`/processes/${props.processId}/user-access/${userId}/remove-excluded`, null)
    );

    async function onRemoveFromExcluded(userId: string) {
        await removeFromExcludedMutation.mutateAsync(userId, {
            onSuccess: () => {
                snackbar.show(translateString("removedFromExcluded"));
            },
        });
    }

    return (
        <ExcludedUsersField
            entity={props.process}
            isLoading={props.isLoading}
            onChange={props.onChange}
            onConfirmNavigate={() => navigate(urlProvider.getProcessesUrl())}
            onRemoveFromExcluded={onRemoveFromExcluded}
            removeFromExcludedInProgress={removeFromExcludedMutation.isLoading}
            disabled={props.disabled}
            isPreview={props.isPreview}
            errorText={props.errorText}
            toolTipText={translateString("processExcludedUsersTooltip")}
            warningText={translateString("processExcludedUsersWarning")}
        />
    );
}

export default ProcessExcludedUsersField;
